import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '@/components/data-table';
import { ClearAllFiltersButton, ToggleFilterButton } from '@/components/data-table-actions';
import { Sidebar } from '@/components/sidebar';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { useToggle } from '@/hooks';
import TaxFilterForm from './TaxFilterForm';
import { clearedParams } from './listParams';

// The app's table writes the page into the address itself; nothing else to do on a page turn.
const noop = () => {};

/**
 * A tab's list as the app's other lists are: the green header with the tab's
 * own buttons, „Filters" (a side panel), „Clear all filters" and the page
 * size; pages come from the server.
 *
 * @param {string} tab - whose filters the panel and the clear button work on
 * @param {React.ReactNode} actions - the tab's buttons, first in the header
 * @param {boolean} filters - false for a tab with nothing to filter but the search
 */
export default function TabTable({
  tab,
  title,
  data,
  total,
  status,
  columns,
  actions,
  projects = [],
  showStatus = true,
  filters = true,
  ...rest
}) {
  const { t } = useTranslation(['tables', 'taxReturns']);
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const { open, setOpen, onClose } = useToggle(false);

  const cleared = useMemo(() => clearedParams(tab), [tab]);
  // The panel's own filters, without the search and sorts the column headers hold.
  const panelDefaults = useMemo(
    () => Object.fromEntries(Object.keys(cleared).filter((k) => k !== 'search' && !k.startsWith('sort')).map((k) => [k, undefined])),
    [cleared]
  );

  return (
    <>
      <DataTable
        title={title}
        data={data}
        total={total}
        status={status}
        columns={columns}
        offset={queryParams.offset}
        limit={queryParams.limit}
        onChangePagination={noop}
        // Wide lists scroll sideways inside the card instead of losing their last column.
        scroll={{ x: 'max-content' }}
        headerChildren={
          <>
            {actions}
            {filters && (
              <ToggleFilterButton label={t('filters', { ns: 'tables' })} onClick={setOpen} defaultParams={panelDefaults} />
            )}
            <ClearAllFiltersButton
              label={t('clearAllFilters', { ns: 'tables' })}
              defaultParams={cleared}
              onReset={() => setQueryParams({ ...cleared, offset: 1 })}
            />
          </>
        }
        {...rest}
      />
      {filters && (
        <Sidebar title={t('filters', { ns: 'tables' })} open={open} onClose={onClose}>
          <TaxFilterForm
            tab={tab}
            projects={projects}
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            onClose={onClose}
            showStatus={showStatus}
          />
        </Sidebar>
      )}
    </>
  );
}
